import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

const DRAFT_KEY = "tresses_booking_draft";

// Clears any in-progress booking draft from sessionStorage and notifies listeners
const clearBookingDraft = () => {
    try {
        sessionStorage.removeItem(DRAFT_KEY);
        window.dispatchEvent(new Event("tresses-draft-changed"));
    } catch { /* ignore */ }
};

// If a draft exists and was created by a different (or any) prior signed-in user,
// drop it so the new account never inherits another customer's selections.
const dropDraftIfForeign = (newEmail) => {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        const d = JSON.parse(raw);
        const owner = d && typeof d === "object" ? d.owner_email : null;
        if (owner && newEmail && owner.toLowerCase() !== newEmail.toLowerCase()) {
            clearBookingDraft();
            return;
        }
        // Stamp ownership on first sign-in so any future sign-in can verify it
        if (!owner && newEmail) {
            sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, owner_email: newEmail.toLowerCase() }));
            window.dispatchEvent(new Event("tresses-draft-changed"));
        }
    } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            dropDraftIfForeign(data?.email);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.access_token) localStorage.setItem("tresses_token", data.access_token);
        setUser(data.user);
        dropDraftIfForeign(data.user?.email);
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data.access_token) localStorage.setItem("tresses_token", data.access_token);
        setUser(data.user);
        // New registration → always start with a clean draft
        clearBookingDraft();
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("tresses_token");
        setUser(null);
        // Intentionally keep the draft in sessionStorage — it's stamped with owner_email
        // so the same user can resume it after logging back in. A different user signing
        // in next will trigger dropDraftIfForeign() and the draft will be discarded.
    };

    const updateProfile = async (payload) => {
        const { data } = await api.patch("/auth/me", payload);
        setUser(data);
        return data;
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, refresh: fetchMe, formatApiError }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
